/* eslint-disable react-refresh/only-export-components */
import { STORAGE } from "@/constants";
import { getItem, setItem } from "@/lib/storage-utils";
import React, { createContext, useContext, useState, useEffect } from "react";

export type Theme = {
  primary: string;
  primaryLight: string;
  primaryExtraLight: string;
  primaryDark: string;
  primaryExtraDark: string;
  primarySelected: string;
  primaryForeground: string;

  secondary: string;
  secondaryLight: string;
  secondaryExtraLight: string;
  secondaryDark: string;
  secondaryExtraDark: string;
  secondarySelected: string;
  secondaryForeground: string;

  fontFamily: string;
};

type ThemeContextType = {
  theme: Theme;
  changeTheme: (theme: Theme) => void;
};

// - These CSS variables are available throughout the app because: They're set on the document root
// - So even without the theme class on App.tsx, the theme changes work because: The CSS variables are being updated globally

const setCSSVariable = (theme: Theme) => {
  // console.log("THEME: setting theme", theme);
  const root = document.documentElement;
  root.style.setProperty("--primary", theme.primary);
  root.style.setProperty("--primary-light", theme.primaryLight);
  root.style.setProperty("--primary-extra-light", theme.primaryExtraLight);
  root.style.setProperty("--primary-dark", theme.primaryDark);
  root.style.setProperty("--primary-extra-dark", theme.primaryExtraDark);
  root.style.setProperty("--primary-selected", theme.primarySelected);
  root.style.setProperty("--primary-foreground", theme.primaryForeground);

  root.style.setProperty("--secondary", theme.secondary);
  root.style.setProperty("--secondary-light", theme.secondaryLight);
  root.style.setProperty("--secondary-extra-light", theme.secondaryExtraLight);
  root.style.setProperty("--secondary-dark", theme.secondaryDark);
  root.style.setProperty("--secondary-extra-dark", theme.secondaryExtraDark);
  root.style.setProperty("--secondary-selected", theme.secondarySelected);
  root.style.setProperty("--secondary-foreground", theme.secondaryForeground);

  root.style.setProperty("--font-family", theme.fontFamily);
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{
  appTheme: Theme;
  children: React.ReactNode;
}> = ({ appTheme, children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = getItem<Theme>("theme", STORAGE.SESSION);
    return savedTheme || appTheme;
  });

  useEffect(() => {
    setCSSVariable(theme);
  }, [theme]);

  const changeTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    setItem("theme", newTheme, STORAGE.LOCAL);
  };

  return (
    <ThemeContext.Provider value={{ theme, changeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
